import React, {useCallback, useState} from 'react';

import {CopyClipboardInputField} from 'app/components/general/CopyClipboardInputField';
import {ConfirmFormPopover} from 'app/general/widgets/ConfirmPopover';
import {gt} from 'app/utils/i18n';

interface Props {
  title: string;
  description: string;
  footer: string | JSX.Element;
  confirmText: string;
  loadingText: string;
  cname: Record<'key' | 'value', string>;
  isLoading: boolean;
  onConfirm: () => void;
  isCanceling?: boolean;
  onCancel?: () => void;
}

export const CNAMEDisplay = ({
  title,
  description,
  footer,
  cname,
  confirmText,
  isLoading,
  loadingText,
  isCanceling,
  onCancel,
  onConfirm,
}: Props) => {
  const [showCancel, setShowCancel] = useState<boolean>(false);

  const [currButtonRef, setCurrButtonRef] = useState<null | HTMLElement>(null);

  const setButtonRef = useCallback((newButtonRef) => {
    if (newButtonRef != currButtonRef) {
      setCurrButtonRef(newButtonRef);
    }
  }, []);

  const popoversOverlayProps = {
    target: currButtonRef as HTMLElement,
    trigger: 'legacy',
    placement: 'bottom' as const,
    isOpen: true,
    toggle: () => setShowCancel(false),
  };

  return (
    <>
      <div>
        <div className="d-flex top-section">
          <h4 className="header flex-1">{title}</h4>
        </div>
        <div>
          <p>{description}</p>
          <div>
            {Object.keys(cname).map((key) => (
              <CopyClipboardInputField
                key={key}
                className="px-0 col-12 col-sm mt-0 flex-grow-1"
                id={`cname-key-${key}`}
                disabled={false}
                label={key}
                value={cname[key]}
              />
            ))}
          </div>
          <p>{footer}</p>
        </div>
      </div>
      {
        <div className="d-flex">
          {onCancel && (
            <button
              ref={setButtonRef}
              className="mt-2 btn btn-link text-danger"
              disabled={isCanceling || showCancel}
              onClick={() => setShowCancel(true)}
            >
              {gt(isCanceling ? 'Resetting...' : 'Reset')}
            </button>
          )}
          <div className="text-right ml-auto">
            <button
              className="mt-2 btn btn-secondary"
              disabled={isLoading}
              onClick={(e) => {
                e.stopPropagation();
                onConfirm();
              }}
            >
              {isLoading ? loadingText : confirmText}
            </button>
          </div>
        </div>
      }
      {showCancel && onCancel ? (
        <ConfirmFormPopover
          overlayProps={popoversOverlayProps}
          text={gt(
            'This will stop the custom domain setup, delete all certificates, and require restarting the process from the beginning.'
          )}
          confirmText={gt('Reset')}
          busyText={gt('Resetting...')}
          onConfirm={onCancel}
          isIdle={true}
        />
      ) : null}
    </>
  );
};
